/**
 * Supplies first-party Agent credentials to the chat session.
 * Checks effective permission before reading the cache or minting credentials.
 * useAgentChatStream.ts routes permission errors to localized panel copy.
 */
import { useCallback } from 'react';

import { useHasEmployeePermission } from 'src/shared/permissions/useCurrentUserPermissions';

import { useMintFirstPartyAgentAccessTokenMutation } from 'src/generated/mloop-graphql';

import { AgentDelegation, AgentDelegationError, agentDelegationStore, parseAgentDelegation } from './delegation';
import type { AgentTokenProvider } from './agentClient';

/**
 * Returns a token provider backed by the `mintFirstPartyAgentAccessToken` session-authenticated mutation.
 *
 * The credential is cached in memory by {@link agentDelegationStore} and re-minted only when it is
 * near expiry or when a caller passes `forceRemint` after a 401 from platform-api.
 */
export default function useAgentDelegationToken(): AgentTokenProvider {
  const { permission } = useHasEmployeePermission('CAN_CREATE_FIRST_PARTY_ACCESS_TOKEN');
  const [mintFirstPartyAgentAccessToken] = useMintFirstPartyAgentAccessTokenMutation({
    fetchPolicy: 'no-cache',
  });

  const mint = useCallback(async (): Promise<AgentDelegation> => {
    const result = await mintFirstPartyAgentAccessToken({ variables: { input: {} } });
    const output = result.data?.mintFirstPartyAgentAccessToken;

    if (!output) {
      throw new AgentDelegationError('INTERNAL', 'Minting an agent delegation returned no result.');
    }
    switch (output.__typename) {
      case 'MintFirstPartyAgentAccessTokenSuccess': {
        const delegation = parseAgentDelegation({
          token: output.token,
          expiresAt: output.expiresAt,
          scopes: output.scopes,
        });
        if (!delegation) {
          throw new AgentDelegationError('INTERNAL', 'Minting an agent delegation returned an unusable credential.');
        }
        return delegation;
      }
      case 'MintFirstPartyAgentAccessTokenPermissionDeniedError':
      case 'MintFirstPartyAgentAccessTokenUnknownError':
        throw new AgentDelegationError(output.errorCode, output.errorMessage, output.errorMessage);
      default:
        throw new AgentDelegationError('INTERNAL', 'Unexpected agent delegation mint result.');
    }
  }, [mintFirstPartyAgentAccessToken]);

  return useCallback(
    async (forceRemint = false) => {
      // Keep the existing token-provider error contract so the Agent stream localizes permission failures.
      if (!permission) {
        throw new AgentDelegationError('PERMISSION_DENIED', 'Agent token permission is required.');
      }
      return await agentDelegationStore.getToken(mint, forceRemint);
    },
    [mint, permission]
  );
}
